import type { RegionOption } from '../../../../shared/regionAccessOptions';
import type { UserRow } from '../userViewModel';

import UserDetailOverlay from './UserDetailOverlay';
import UsersFilterBar, { type UsersFilterState } from './UsersFilterBar';
import UsersKpiStrip from './UsersKpiStrip';
import UsersListPanelFooter from './UsersListPanelFooter';
import UsersTable from './UsersTable';

export interface UsersKpi {
  label: string;
  value: number | string;
  sub: string;
  warn?: boolean;
  /** When set, the value is placeholder pending the named backend work. */
  mockedPending?: string;
}

interface UsersListPanelProps {
  kpis: UsersKpi[];
  rows: UserRow[];
  totalInScope: number;
  selected: UserRow | null;
  filters: UsersFilterState;
  onFiltersChange: (next: UsersFilterState) => void;
  onSelect: (row: UserRow) => void;
  onClearSelection: () => void;
  scopedRegionLabel: string | null;
  manageableRegionIds: string[] | null;
  regionOptions: RegionOption[];
  importedCount: number;
  onDeactivate?: (user: UserRow) => void;
  onReactivate?: (user: UserRow) => void;
  onEdit?: (user: UserRow) => void;
  /** Per-row "Send invite" for imported users — opens the invite drawer
   *  pre-linked to that user. */
  onInvite?: (user: UserRow) => void;
  /** Per-row actions on invited users. */
  onResendInvite?: (user: UserRow) => void;
  onRevokeInvite?: (user: UserRow) => void;
}

/** The list body: KPI strip + table + footer + (floating) detail pane.
 *  Renders the full filtered row set in one go — the table scrolls
 *  internally and the footer just summarizes counts (no pagination). */
export default function UsersListPanel({
  kpis,
  rows,
  totalInScope,
  selected,
  filters,
  onFiltersChange,
  onSelect,
  onClearSelection,
  scopedRegionLabel,
  manageableRegionIds,
  regionOptions,
  importedCount,
  onDeactivate,
  onReactivate,
  onEdit,
  onInvite,
  onResendInvite,
  onRevokeInvite,
}: UsersListPanelProps) {
  return (
    <>
      <UsersKpiStrip items={kpis} />

      <div className="relative flex-1 min-h-0 flex overflow-hidden">
        <section
          className="flex-1 min-w-0 bg-white dark:bg-[#1a1f1c] rounded-xl border border-stone-200 dark:border-stone-800 flex flex-col"
          aria-hidden={selected ? true : undefined}
        >
          <UsersFilterBar
            filters={filters}
            onChange={onFiltersChange}
            scopedRegionLabel={scopedRegionLabel}
            regionOptions={regionOptions}
          />
          <UsersTable
            rows={rows}
            selectedId={selected?.id ?? null}
            onSelect={onSelect}
            onInvite={onInvite}
            onResendInvite={onResendInvite}
            onRevokeInvite={onRevokeInvite}
            manageableRegionIds={manageableRegionIds}
          />
          <UsersListPanelFooter
            visibleCount={rows.length}
            totalInScope={totalInScope}
            importedCount={importedCount}
          />
        </section>

        {selected && (
          <UserDetailOverlay
            user={selected}
            onClose={onClearSelection}
            onDeactivate={onDeactivate}
            onReactivate={onReactivate}
            onEdit={onEdit}
          />
        )}
      </div>
    </>
  );
}
